import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Router, RouterOutlet } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subject, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, catchError, of, tap } from 'rxjs';
import { CatalogService } from '@htx/catalog';
import { DiscoveryApiPort, OrganizationSearchResult } from '@htx/discovery';
import { IntroductionBoxComponent } from '../../../../../core/introduction-box/introduction-box.component';

const BPNL_PATTERN = /^BPNL\d{12}$/;

@Component({
  selector: 'app-catalog',
  imports: [
    TranslocoPipe,
    RouterOutlet,
    AsyncPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    IntroductionBoxComponent
  ],
  templateUrl: './catalog.component.html',
  styleUrl: './catalog.component.scss'
})
export class CatalogComponent implements OnInit, OnDestroy {
  readonly router = inject(Router);
  readonly catalogService = inject(CatalogService);
  readonly discoveryApi = inject(DiscoveryApiPort);
  readonly transloco = inject(TranslocoService);
  readonly loading$ = this.catalogService.loading$;

  readonly searchControl = new FormControl('');

  /** Whether the current input looks like a BPNL */
  isBpnlMode = signal(false);

  /** Organization search results */
  orgResults = signal<OrganizationSearchResult[]>([]);

  /** Whether org search is in progress */
  orgSearching = signal(false);

  /** Whether org search returned empty */
  orgNoResults = signal(false);

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      // mat-autocomplete sets value to the OrganizationSearchResult object on select;
      // normalize to string before any .trim() calls to prevent TypeError killing the subscription
      map(value => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') return (value as unknown as OrganizationSearchResult).organizationName ?? '';
        return '';
      }),
      tap(value => {
        const trimmed = value.trim();
        const looksLikeBpnl = /^BPNL/i.test(trimmed);
        this.isBpnlMode.set(looksLikeBpnl);

        // Auto-search if a valid BPNL is pasted/typed
        if (BPNL_PATTERN.test(trimmed.toUpperCase())) {
          this.orgResults.set([]);
          this.orgNoResults.set(false);
          this.catalogService.searchByBpnl(trimmed.toUpperCase());
          return;
        }

        // Clear org results if input too short or looks like BPNL
        if (trimmed.length < 2 || looksLikeBpnl) {
          this.orgResults.set([]);
          this.orgNoResults.set(false);
        }
      }),
      filter(value => {
        const trimmed = value.trim();
        return trimmed.length >= 2 && !/^BPNL/i.test(trimmed);
      }),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.orgSearching.set(true);
        this.orgNoResults.set(false);
      }),
      switchMap(value =>
        this.discoveryApi.searchOrganizations(value!.trim()).pipe(
          catchError(() => of([]))
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.orgSearching.set(false);
      this.orgResults.set(results);
      this.orgNoResults.set(results.length === 0);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOrgSelected(event: MatAutocompleteSelectedEvent): void {
    const org = event.option.value as OrganizationSearchResult;
    this.searchControl.setValue(org.organizationName, { emitEvent: false });
    this.orgResults.set([]);
    this.catalogService.searchByBpnl(org.bpnl);
  }

  /** Display function for mat-autocomplete (shows org name, not the object) */
  displayOrg(org: OrganizationSearchResult | string): string {
    if (typeof org === 'string') return org;
    return org?.organizationName ?? '';
  }

  /** Clear the search field and reset state */
  clearSearch(): void {
    this.searchControl.setValue('');
    this.orgResults.set([]);
    this.orgNoResults.set(false);
    this.isBpnlMode.set(false);
  }

  /** Manual search trigger (Enter key) */
  searchCatalog(): void {
    const value = typeof this.searchControl.value === 'string'
      ? this.searchControl.value.trim()
      : '';
    if (!value) return;

    // If it's a valid BPNL, search directly
    if (BPNL_PATTERN.test(value.toUpperCase())) {
      this.catalogService.searchByBpnl(value.toUpperCase());
      return;
    }

    // If there's exactly one org result, use it
    const results = this.orgResults();
    if (results.length === 1) {
      this.searchControl.setValue(results[0].organizationName, { emitEvent: false });
      this.orgResults.set([]);
      this.catalogService.searchByBpnl(results[0].bpnl);
    }
  }
}
